import { useRef, useState, type ReactNode } from 'react'
import { motion, useInView, useMotionValueEvent, useReducedMotion, useScroll, type Variants } from 'motion/react'
import { cn } from '@/lib/utils'
import { useMediaQuery } from '@/components/motion'
import { BRAND, OUT_EASE, WIPE_EASE, type BrandColor } from '@/components/landing/service/halftone'

export interface MirroredStep {
  title: string
  description: string
  /** Square visual; `active` is true while the step is on stage (or in view in flow mode). */
  visual: (active: boolean) => ReactNode
}

/** Scroll distance each step holds the stage, in viewport heights. */
const STEP_SCROLL = 130

const textVariants: Variants = {
  before: { opacity: 0, x: -24, transition: { duration: 0.35, ease: OUT_EASE } },
  after: { opacity: 0, x: 24, transition: { duration: 0.35, ease: OUT_EASE } },
  active: (k: number) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: OUT_EASE, delay: 0.18 + k * 0.07 },
  }),
}

const pad = (n: number) => String(n + 1).padStart(2, '0')

/**
 * The pinned feature stage, mirrored: the square visuals sit on the LEFT and
 * open as a wipe from the left edge (clip-path inset from the right), the
 * step copy sits on the RIGHT and slides sideways between steps. A vertical
 * progress rail in the accent runs down the visual's outer edge, and a
 * step index (01 / 02 / 03) under the copy marks where you are.
 *
 * CSS sticky + motion useScroll. Below lg and under reduced motion the steps
 * render in normal flow, visual first, complete and static.
 */
export function MirroredSteps({
  steps,
  accent = 'purple',
  className,
}: {
  steps: MirroredStep[]
  accent?: BrandColor
  className?: string
}) {
  const wide = useMediaQuery('(min-width: 1024px)')
  const reduced = useReducedMotion()
  if (!wide || reduced) return <FlowSteps steps={steps} accent={accent} className={className} />
  return <PinnedSteps steps={steps} accent={accent} className={className} />
}

function PinnedSteps({ steps, accent, className }: { steps: MirroredStep[]; accent: BrandColor; className?: string }) {
  const trackRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const count = steps.length
  const [active, setActive] = useState(0)
  const entered = useInView(stageRef, { once: true, amount: 0.5 })
  const { scrollYProgress } = useScroll({ target: trackRef, offset: ['start start', 'end end'] })

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const next = Math.min(count - 1, Math.max(0, Math.floor(v * count)))
    setActive((prev) => (prev === next ? prev : next))
  })

  return (
    <div ref={trackRef} className={cn('relative', className)} style={{ height: `calc(100svh + ${count * STEP_SCROLL}svh)` }}>
      <div ref={stageRef} className="sticky top-0 flex h-[100svh] items-center overflow-hidden">
        <div className="landing-container grid grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] items-center gap-16 xl:gap-24">
          {/* Visuals: stacked squares wiping in from the left, rail on the outer edge */}
          <div className="flex w-full max-w-[calc(100svh-10rem)] gap-4">
            <div className="w-1 shrink-0 bg-[var(--color-landing-gray-200)] dark:bg-[var(--color-landing-gray-800)]" aria-hidden>
              <motion.div className="h-full w-full origin-top" style={{ scaleY: scrollYProgress, backgroundColor: BRAND[accent] }} />
            </div>
            <div className="relative aspect-square w-full">
              {steps.map((step, i) => {
                const open = i === 0 ? entered : i <= active
                return (
                  <motion.div
                    key={step.title}
                    className="absolute inset-0"
                    initial={false}
                    animate={{ clipPath: open ? 'inset(0% 0% 0% 0%)' : 'inset(0% 100% 0% 0%)' }}
                    transition={{ duration: 0.8, ease: WIPE_EASE }}
                  >
                    {step.visual(entered && i === active)}
                  </motion.div>
                )
              })}
            </div>
          </div>

          {/* Text: every step in one grid cell, sliding sideways */}
          <div>
            <div className="grid">
              {steps.map((step, i) => {
                const state = i === active ? 'active' : i < active ? 'before' : 'after'
                return (
                  <motion.div
                    key={step.title}
                    initial={false}
                    animate={state}
                    className={cn('col-start-1 row-start-1', i !== active && 'pointer-events-none')}
                    aria-current={i === active ? 'step' : undefined}
                  >
                    <motion.div variants={textVariants} custom={0}>
                      <h3 className="landing-text-display-lg text-[var(--color-landing-gray-900)] dark:text-white">{step.title}</h3>
                    </motion.div>
                    <motion.p
                      variants={textVariants}
                      custom={1}
                      className="landing-text-body-lg mt-5 max-w-[44ch] text-pretty text-[var(--color-landing-gray-500)] dark:text-[var(--color-landing-gray-400)]"
                    >
                      {step.description}
                    </motion.p>
                  </motion.div>
                )
              })}
            </div>
            <ol aria-hidden className="mt-12 flex gap-6">
              {steps.map((step, i) => (
                <li
                  key={step.title}
                  className={cn(
                    'border-t-2 pt-3 font-[family-name:var(--font-landing-subdisplay)] text-2xl font-light tracking-[-0.02em] transition-colors duration-300',
                    i === active
                      ? 'border-current text-[var(--color-landing-gray-900)] dark:text-white'
                      : 'border-[var(--color-landing-gray-200)] text-[var(--color-landing-gray-300)] dark:border-[var(--color-landing-gray-800)] dark:text-[var(--color-landing-gray-700)]'
                  )}
                  style={i === active ? { color: BRAND[accent] } : undefined}
                >
                  {pad(i)}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  )
}

function FlowVisual({ render }: { render: MirroredStep['visual'] }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.4 })
  return (
    <div ref={ref} className="relative aspect-square w-full max-w-xl">
      {render(inView)}
    </div>
  )
}

function FlowSteps({ steps, accent, className }: { steps: MirroredStep[]; accent: BrandColor; className?: string }) {
  return (
    <ol className={cn('landing-container space-y-20 md:space-y-28', className)}>
      {steps.map((step, i) => (
        <li key={step.title} className="grid items-center gap-8 md:grid-cols-2 md:gap-12">
          <FlowVisual render={step.visual} />
          <div>
            <span
              aria-hidden
              className="block border-t-2 pt-3 font-[family-name:var(--font-landing-subdisplay)] text-2xl font-light tracking-[-0.02em]"
              style={{ borderColor: BRAND[accent], color: BRAND[accent] }}
            >
              {pad(i)}
            </span>
            <h3 className="landing-text-display-lg mt-6 text-[var(--color-landing-gray-900)] dark:text-white">{step.title}</h3>
            <p className="landing-text-body-lg mt-5 max-w-[46ch] text-pretty text-[var(--color-landing-gray-500)] dark:text-[var(--color-landing-gray-400)]">
              {step.description}
            </p>
          </div>
        </li>
      ))}
    </ol>
  )
}

export default MirroredSteps
